/**
 * ICENFIRE — Duality state (storage + toggles + gateway chrome)
 * Theme swaps use View Transitions when available; never transition `*`.
 */
class IcenfireThemeManager {
  constructor() {
    this.storageKey = 'icenfire_theme';
    this.cookieName = 'icenfire_theme';
    this.validThemes = ['ice', 'fire', 'void'];
    this.body = document.body;
    this.root = document.documentElement;
    this.init();
  }

  init() {
    const saved = this.getSavedTheme();
    if (saved) {
      this.applyTheme(saved, { animate: false });
      this.dismissGatewayImmediate();
    } else {
      this.applyTheme('ice', { animate: false, persist: false });
      this.openGateway();
    }
    this.bindToggleEvents();
  }

  getSavedTheme() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (this.validThemes.includes(stored)) return stored;
    } catch (_) {
      const match = document.cookie.match(new RegExp('(^| )' + this.cookieName + '=([^;]+)'));
      if (match && this.validThemes.includes(match[2])) return match[2];
    }
    return null;
  }

  saveTheme(theme) {
    try {
      localStorage.setItem(this.storageKey, theme);
    } catch (_) {}
    document.cookie = `${this.cookieName}=${theme}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
  }

  paint(theme) {
    ['ice', 'fire', 'void'].forEach((t) => {
      this.body.classList.remove(`theme-${t}`);
      this.root.classList.remove(`theme-${t}`);
    });
    this.body.classList.add(`theme-${theme}`);
    this.root.classList.add(`theme-${theme}`);
    this.root.setAttribute('data-theme', theme);
    this.updateToggles(theme);
  }

  applyTheme(theme, { animate = true, persist = true } = {}) {
    if (!this.validThemes.includes(theme)) return;

    const commit = () => {
      this.paint(theme);
      if (persist !== false) this.saveTheme(theme);
      window.dispatchEvent(new CustomEvent('icenfire:themechange', { detail: { theme } }));
    };

    if (animate && document.startViewTransition) {
      document.startViewTransition(commit);
    } else if (animate) {
      this.root.classList.add('theme-crossfade');
      commit();
      window.setTimeout(() => this.root.classList.remove('theme-crossfade'), 420);
    } else {
      commit();
    }
  }

  toggleTheme() {
    const next = this.root.getAttribute('data-theme') === 'fire' ? 'ice' : 'fire';
    this.applyTheme(next);
  }

  bindToggleEvents() {
    document.querySelectorAll('[data-theme-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = btn.getAttribute('data-theme-target');
        if (target) this.applyTheme(target);
        else this.toggleTheme();
      });
    });
  }

  updateToggles(activeTheme) {
    document.querySelectorAll('[data-theme-switcher]').forEach((switcher) => {
      const thumbTheme = activeTheme === 'void' ? 'ice' : activeTheme;
      switcher.setAttribute('data-active-theme', thumbTheme);
      switcher.classList.toggle('is-void', activeTheme === 'void');
      switcher.querySelectorAll('[data-theme-target]').forEach((btn) => {
        const on = btn.getAttribute('data-theme-target') === activeTheme;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.classList.toggle('is-active', on);
      });
    });
  }

  dismissGatewayImmediate() {
    const gateway = document.querySelector('[data-icenfire-gateway]');
    this.root.classList.remove('gateway-pending');
    this.root.classList.add('gateway-resolved');
    this.body.classList.remove('gateway-pending');
    if (gateway) {
      gateway.setAttribute('aria-hidden', 'true');
      gateway.classList.remove('is-committing');
      gateway.removeAttribute('data-commit');
    }
  }

  openGateway() {
    const gateway = document.querySelector('[data-icenfire-gateway]');
    if (!gateway) return;
    this.root.classList.remove('gateway-resolved');
    this.root.classList.add('gateway-pending');
    this.body.classList.add('gateway-pending');
    gateway.setAttribute('aria-hidden', 'false');
    gateway.classList.remove('is-committing');
    gateway.removeAttribute('data-commit');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.IcenfireTheme = new IcenfireThemeManager();
});
